/**
 * TermsAndConditionsPage — public-facing NIPOST terms of service.
 *
 * Pure static content ported from `screens/src/pages/legal/TermsAndConditions.jsx`.
 * No API calls. Mounted at `/legal/terms` in App.tsx — outside the authenticated
 * portal so anyone can read it before they register.
 */
import type { JSX } from 'react';

import { LegalLayout, type LegalSection } from '../../layouts/LegalLayout';

const SECTIONS: readonly LegalSection[] = [
  { id: 'acceptance',          label: 'Acceptance of these terms' },
  { id: 'definitions',         label: 'Definitions' },
  { id: 'eligibility',         label: 'Eligibility' },
  { id: 'account',             label: 'Your account' },
  { id: 'kyc',                 label: 'Identity verification (KYC)' },
  { id: 'mail',                label: 'Mail & parcel services' },
  { id: 'prohibited-items',    label: 'Prohibited & restricted items' },
  { id: 'pay',                 label: 'NIPOST Pay wallet' },
  { id: 'government-services', label: 'Government services' },
  { id: 'pricing',             label: 'Pricing, fees & taxes' },
  { id: 'payments',            label: 'Payments & receipts' },
  { id: 'refunds',             label: 'Refunds, returns & claims' },
  { id: 'acceptable-use',      label: 'Acceptable use' },
  { id: 'availability',        label: 'Service availability' },
  { id: 'ip',                  label: 'Intellectual property' },
  { id: 'liability',           label: 'Liability & disclaimers' },
  { id: 'indemnity',           label: 'Indemnity' },
  { id: 'termination',         label: 'Termination & suspension' },
  { id: 'governing-law',       label: 'Governing law & disputes' },
  { id: 'changes',             label: 'Changes to these terms' },
  { id: 'contact',             label: 'Contact us' },
];

export function TermsAndConditionsPage(): JSX.Element {
  return (
    <LegalLayout
      title="Terms & Conditions"
      subtitle="The rules that apply when you use NIPOST services — including parcel delivery, NIPOST Pay, KYC verification and the government services available at our outlets."
      effective="27 April 2026"
      lastUpdated="27 April 2026"
      sections={SECTIONS}
      sibling={{ to: '/legal/privacy', label: 'Read the Privacy Policy' }}
    >
      <div className="callout">
        <strong>Plain-language summary.</strong> These Terms govern your use of NIPOST services.
        They cover who can use the service, how your account works, what you may and may not do,
        what happens if a parcel is delayed or lost, how NIPOST Pay works (with CBN-aligned
        wallet limits), pricing and refunds, and how disputes are resolved under Nigerian law.
        Read them carefully — by using NIPOST you agree to them.
      </div>

      <h2 id="acceptance">1. Acceptance of these terms</h2>
      <p>
        These Terms & Conditions ("<strong>Terms</strong>") form a binding contract between you
        and the Nigerian Postal Service ("<strong>NIPOST</strong>", "we", "us", "our"). By
        creating a NIPOST account, posting a parcel through us, opening a NIPOST Pay wallet, or
        otherwise using our services, you confirm that you have read, understood and agreed to be
        bound by these Terms and by our <a href="/legal/privacy">Privacy Policy</a>.
      </p>
      <p>
        If you are accepting these Terms on behalf of a business, you confirm that you have the
        authority to bind that business, and references to "you" include the business.
      </p>

      <h2 id="definitions">2. Definitions</h2>
      <ul>
        <li><strong>"Services"</strong> means the parcel, retail, financial, government and identity services NIPOST provides through any channel — online, mobile, at a post office, or at a NIPOST franchise outlet.</li>
        <li><strong>"Outlet"</strong> means a NIPOST post office, a Tier 1 Full Post Office franchise, a Tier 2 Postal Agent, or a Tier 3 Last-Mile Point.</li>
        <li><strong>"Account"</strong> means your registered profile on the NIPOST customer portal or app.</li>
        <li><strong>"Wallet"</strong> means a NIPOST Pay account held by you within the limits prescribed by the Central Bank of Nigeria.</li>
        <li><strong>"Mail item"</strong> means any letter, document or parcel handed to NIPOST or one of its outlets for transit.</li>
        <li><strong>"Tracking number"</strong> means the 13-character UPU S10 number assigned to your mail item.</li>
        <li><strong>"KYC"</strong> means the customer identity-verification process carried out under Nigerian and CBN AML/CFT rules.</li>
      </ul>

      <h2 id="eligibility">3. Eligibility</h2>
      <p>To use NIPOST Services you must:</p>
      <ul>
        <li>be at least <strong>18 years of age</strong>, or, if a minor, act through a parent or legal guardian who accepts these Terms on your behalf;</li>
        <li>have legal capacity to enter into a binding contract under Nigerian law;</li>
        <li>provide accurate, current and complete information when you register;</li>
        <li>not be on any sanctions list, watchlist or politically exposed persons screening list that would prohibit us from contracting with you.</li>
      </ul>
      <p>We may decline to provide Services where we are not satisfied that these eligibility conditions are met.</p>

      <h2 id="account">4. Your account</h2>
      <h3>4.1 Registration</h3>
      <p>
        You are responsible for the accuracy of the information you provide. You must keep your
        registered phone number, email address and identity details up to date.
      </p>
      <h3>4.2 Security</h3>
      <p>
        You are responsible for keeping your password, PIN, OTP codes and biometric
        authenticators confidential. You must enable multi-factor authentication where prompted,
        and you must notify us immediately if you believe your account has been compromised.
      </p>
      <h3>4.3 One person, one account</h3>
      <p>
        You may hold only one personal NIPOST customer account. Businesses may register
        additional accounts for staff, but each individual must have their own credentials.
      </p>

      <h2 id="kyc">5. Identity verification (KYC)</h2>
      <p>
        Some Services — particularly NIPOST Pay, government services and high-value parcel
        handling — require us to verify your identity to a tier specified by the CBN, the NDPA
        and other applicable laws. Verification tiers are:
      </p>
      <table>
        <thead>
          <tr><th>Tier</th><th>Identity required</th><th>Example services</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>BASIC</strong></td><td>Verified mobile number; basic personal details</td><td>Wallet open; small-value transfers within CBN limits</td></tr>
          <tr><td><strong>STANDARD</strong></td><td>NIN + matched biometric</td><td>Higher transfer limits; bill payments; parcel send/receive</td></tr>
          <tr><td><strong>ENHANCED</strong></td><td>NIN + BVN + biometrics + address verification</td><td>Highest wallet limits; international transfers; commercial volumes</td></tr>
        </tbody>
      </table>
      <p>
        We may decline a transaction, place a hold or close an account where we cannot complete
        identity verification, where we detect a sanctions or AML hit, or where we are required
        to do so by a regulator or court.
      </p>

      <h2 id="mail">6. Mail & parcel services</h2>
      <h3>6.1 Acceptance</h3>
      <p>
        We accept items for transit subject to inspection, weighing, declared value and pricing
        in accordance with the published NIPOST tariff. By handing an item to NIPOST you confirm
        that the contents are lawful, accurately described, and not on the prohibited or
        restricted list (Section 7).
      </p>
      <h3>6.2 Delivery times</h3>
      <p>
        Delivery times for standard mail, EMS Speedpost and other services are{' '}
        <strong>indicative</strong> and depend on origin, destination, weather, security
        conditions and customs (for international mail). NIPOST will use reasonable endeavours to
        meet the published target but does not guarantee delivery within a specific time unless
        an EMS guarantee is purchased.
      </p>
      <h3>6.3 Tracking</h3>
      <p>
        You may track your mail item using the 13-character S10 tracking number issued at
        registration. Tracking events are best-effort and may be delayed during peak periods or
        where the item is in transit between hubs.
      </p>
      <h3>6.4 Proof of delivery</h3>
      <p>
        Delivery is evidenced by the recipient's signature (always required) and, for
        higher-value or non-self deliveries, a photograph of the parcel and the GPS location at
        the time of delivery.
      </p>
      <h3>6.5 Liability for loss or damage</h3>
      <p>Subject to Section 16, NIPOST's liability for loss or damage to a mail item is limited to:</p>
      <ul>
        <li>the value declared at the point of acceptance, up to the maximum stated in the published tariff; or</li>
        <li>where no value is declared, the statutory minimum compensation under the Nigerian Postal Service Act and the Universal Postal Union conventions for international items.</li>
      </ul>
      <p>We do not accept liability for indirect, consequential, or business losses (including loss of profit or opportunity).</p>

      <h2 id="prohibited-items">7. Prohibited & restricted items</h2>
      <p>You must not post, ship or accept the following:</p>
      <ul>
        <li>narcotic drugs, psychotropic substances and any controlled substances;</li>
        <li>firearms, ammunition, explosives, fireworks and any military-grade items;</li>
        <li>flammable, corrosive, radioactive or otherwise hazardous goods (Class 1–9 dangerous goods, except where you have arranged a licensed dangerous-goods service);</li>
        <li>currency, gold, precious metals, gemstones and bearer instruments above the limits in the tariff;</li>
        <li>perishable goods that cannot survive the published transit time;</li>
        <li>live animals (except where a specialised service has been arranged);</li>
        <li>obscene, defamatory, hateful or seditious material;</li>
        <li>any item the import, export or possession of which is prohibited by Nigerian law or the law of the destination country.</li>
      </ul>
      <p>
        We may refuse, open, hold, return to sender, destroy, hand over to a regulator or
        law-enforcement agency, or otherwise dispose of any item that we reasonably believe to be
        on this list. You indemnify NIPOST against any claim arising from such an item.
      </p>

      <h2 id="pay">8. NIPOST Pay wallet</h2>
      <p>
        NIPOST Pay is a digital wallet operated under licence and in line with the Central Bank
        of Nigeria's framework for payment service operators. By opening a wallet you accept the
        following.
      </p>
      <h3>8.1 Wallet limits</h3>
      <p>
        Daily, single-transaction and cumulative balance limits are governed by your KYC tier and
        updated whenever the CBN issues new guidance. Limits are displayed in the NIPOST Pay app
        and may be lowered without notice for risk-management reasons.
      </p>
      <h3>8.2 Funding & withdrawal</h3>
      <p>
        You may fund your wallet by card, bank transfer, cash deposit at a NIPOST outlet, or any
        other channel we make available. Withdrawals are made to a verified bank account or by
        cash-out at an authorised NIPOST agent.
      </p>
      <h3>8.3 Failed and reversed transactions</h3>
      <p>
        We process refunds and reversals according to the rules of the relevant payment scheme
        and the CBN's Consumer Protection Framework. Reversal timelines depend on the
        counterparty. Where a transaction is suspected fraudulent we may freeze the disputed
        amount pending investigation.
      </p>
      <h3>8.4 Dormant wallets</h3>
      <p>
        Wallets that show no customer-initiated activity for 12 months may be flagged as dormant.
        Wallets dormant for over 24 months may be subject to balance treatment in line with CBN's
        Unclaimed Balances rules.
      </p>
      <h3>8.5 No interest, no investment</h3>
      <p>
        Your wallet balance is held safely under CBN rules but is <strong>not</strong> a deposit
        and does <strong>not</strong> accrue interest. Wallet balances are not a substitute for a
        bank account, an investment or a credit facility.
      </p>

      <h2 id="government-services">9. Government services</h2>
      <p>
        NIPOST acts as an <strong>agent</strong> of the issuing government agency (e.g. NIMC for
        NIN, NIS for passports, FRSC for driver's licences, FIRS for tax payments). We collect
        your application, verify your identity, take payment and submit the application to the
        agency. The decision to issue, deny, renew or revoke a service remains with the agency,
        which has its own terms.
      </p>
      <p>
        Service-level timelines, fees and refund rules for government services are set by the
        relevant agency and not by NIPOST.
      </p>

      <h2 id="pricing">10. Pricing, fees & taxes</h2>
      <p>
        Prices are quoted at the point of transaction and shown on your receipt. Prices are
        inclusive of VAT and statutory charges unless stated otherwise. We may change tariffs
        from time to time; the price applicable to your transaction is the price displayed at the
        point of acceptance.
      </p>

      <h2 id="payments">11. Payments & receipts</h2>
      <p>
        We accept cash, card (POS), bank transfer, NIPOST Pay wallet, and any other channel we
        make available. A receipt is issued for every paid transaction; please keep your receipt
        as proof of payment for any later claim.
      </p>

      <h2 id="refunds">12. Refunds, returns & claims</h2>
      <h3>12.1 Mail & parcel claims</h3>
      <p>
        Claims for loss, damage, delay, mis-delivery or non-receipt must be lodged within{' '}
        <strong>30 days</strong> of the expected delivery date for domestic items and{' '}
        <strong>90 days</strong> for international items. You must provide the original waybill,
        your receipt and reasonable evidence of the loss or damage. Compensation is paid in line
        with Section 6.5.
      </p>
      <h3>12.2 Wallet & payment refunds</h3>
      <p>
        Refunds are processed within the timelines mandated by the CBN's Consumer Protection
        Framework — typically up to 5 business days for domestic transactions, longer for
        international. We will keep you informed where investigation is required.
      </p>
      <h3>12.3 Government services refunds</h3>
      <p>
        Where a government service application has been submitted to an agency, refundability is
        governed by that agency. NIPOST refunds the portion retained as service fee where we are
        unable to submit the application due to our own error.
      </p>

      <h2 id="acceptable-use">13. Acceptable use</h2>
      <p>You must not use NIPOST Services to:</p>
      <ul>
        <li>break any Nigerian law or any law of the destination country for an international shipment;</li>
        <li>send, receive, finance or facilitate money laundering, terrorism financing, fraud, tax evasion, or sanctioned activity;</li>
        <li>impersonate another person, misrepresent your identity, or interfere with another customer's account;</li>
        <li>introduce malware, attempt to compromise our systems, scrape, reverse-engineer or otherwise abuse our platforms;</li>
        <li>use NIPOST Services to harass, threaten or harm any person;</li>
        <li>send any item on the prohibited or restricted list (Section 7).</li>
      </ul>
      <p>
        Breach of acceptable use may result in suspension, account closure, forfeiture of items
        in transit, reporting to the relevant authorities and criminal or civil proceedings.
      </p>

      <h2 id="availability">14. Service availability</h2>
      <p>
        We aim to keep our online services available at all times but maintenance, security
        incidents, network outages and force-majeure events may interrupt access. NIPOST is not
        liable for any temporary unavailability provided we have used reasonable endeavours to
        restore the service.
      </p>

      <h2 id="ip">15. Intellectual property</h2>
      <p>
        The NIPOST name, logo, brand assets, the UPMP platform, NIPOST Pay, software, designs,
        and all related content are protected intellectual property of NIPOST or its licensors.
        You may not copy, modify, reverse-engineer, redistribute or commercially exploit any of
        it without our prior written consent. You retain ownership of the content you submit to
        us (for example, identity documents) but grant NIPOST a non-exclusive licence to use that
        content as necessary to deliver the Services.
      </p>

      <h2 id="liability">16. Liability & disclaimers</h2>
      <p>Nothing in these Terms limits any liability that cannot be limited under Nigerian law. Subject to that:</p>
      <ul>
        <li>NIPOST's total aggregate liability arising from or in connection with the Services is limited to the higher of (i) the declared value of the affected item, (ii) the wallet balance affected by the breach, or (iii) NGN 100,000;</li>
        <li>we exclude liability for indirect, consequential, special, incidental or punitive damages, including loss of profit, opportunity, goodwill or data;</li>
        <li>we provide the Services on an "as is" basis without warranties of merchantability, fitness for a particular purpose or non-infringement, except as expressly stated.</li>
      </ul>

      <h2 id="indemnity">17. Indemnity</h2>
      <p>
        You agree to indemnify, defend and hold harmless NIPOST, its officers, employees,
        franchisees, agents and platform partners from any claim, loss, liability, damage, fine
        or expense (including reasonable legal fees) arising from your breach of these Terms,
        your misuse of the Services, your shipment of a prohibited or restricted item, or your
        violation of any applicable law.
      </p>

      <h2 id="termination">18. Termination & suspension</h2>
      <p>We may suspend or terminate your account at any time if:</p>
      <ul>
        <li>you breach these Terms or any applicable law;</li>
        <li>we are required to do so by a regulator, a court or a law-enforcement agency;</li>
        <li>we reasonably suspect your account is compromised, used fraudulently or used for money laundering;</li>
        <li>you have not used your account for an extended period (see Section 8.4 for wallets); or</li>
        <li>continued provision of Services to you is no longer commercially or legally feasible.</li>
      </ul>
      <p>
        On termination, we will return any wallet balance to your verified bank account, less any
        outstanding amount you owe NIPOST, and we will retain personal data only for as long as
        the Privacy Policy requires.
      </p>

      <h2 id="governing-law">19. Governing law & dispute resolution</h2>
      <p>
        These Terms are governed by the laws of the{' '}
        <strong>Federal Republic of Nigeria</strong>. We hope to resolve any dispute amicably;
        please contact our customer service in the first instance. If a dispute cannot be
        resolved within 30 days of being raised, it shall be referred to:
      </p>
      <ol>
        <li>mediation in good faith between you and NIPOST; failing which</li>
        <li>arbitration under the Arbitration and Mediation Act 2023 of Nigeria, seated in Abuja, in the English language, before a single arbitrator agreed between the parties or appointed by the President of the Chartered Institute of Arbitrators (Nigeria); or</li>
        <li>in matters not capable of arbitration, the exclusive jurisdiction of the competent Federal High Court sitting in Abuja.</li>
      </ol>
      <p>
        Nothing in this Section restricts your right to pursue a complaint with the NDPC, the CBN
        Consumer Protection Department, the Federal Competition and Consumer Protection
        Commission (FCCPC) or any other competent regulator.
      </p>

      <h2 id="changes">20. Changes to these terms</h2>
      <p>
        We may update these Terms from time to time. The "Last updated" date at the top of this
        page indicates when the latest revision was published. Where a change materially affects
        your rights, we will give you at least <strong>14 days' notice</strong> through your
        registered email or in-app notification. Continued use of the Services after the
        effective date constitutes acceptance of the updated Terms.
      </p>

      <h2 id="contact">21. Contact us</h2>
      <ul>
        <li><strong>General enquiries:</strong> <a href="mailto:support@nipost.gov.ng">support@nipost.gov.ng</a></li>
        <li><strong>Customer support:</strong> 0800-NIPOST (0800-647-678) · 9:00–17:00 WAT, Monday–Saturday</li>
        <li><strong>Legal & disputes:</strong> <a href="mailto:legal@nipost.gov.ng">legal@nipost.gov.ng</a></li>
        <li><strong>Postal address:</strong> NIPOST Headquarters, Garki Area 1, Abuja, FCT, Nigeria</li>
      </ul>

      <div className="callout">
        <strong>Document status.</strong> These Terms have been prepared as part of the UPMP
        roll-out and will be reviewed and signed off by NIPOST's legal counsel before final
        publication. Until that review is complete, treat this document as a working draft.
      </div>
    </LegalLayout>
  );
}
